// @react
import React from 'react'
// @types
import type { MemberBenefitTab } from 'modules/benefitsCenter/types'
import type {
  AlgoliaContentPayload,
  IAbstractSearchItem,
} from 'lib/graphql/graphqlTypes'
// @libraries
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
// @components
import Box from '@bit/atd.web.box'
import Accordion from '@bit/atd.web.accordion'
import Button from '@bit/atd.web.button'
import Spacer from '@bit/atd.web.spacer'
import Carousel from '@bit/atd.web.carousel'
// @module components
import BadgedLabel from '../BadgedLabel'
// @module common components
import Courses from 'modules/benefitsCenter/components/MemberBenefits/Courses'
import CustomCardHit from 'modules/search/components/Widgets/CustomCardHit'
// @config
import { tabIndexes, callCustomerCodes, tabsConfig } from '../benefitsConfig'

const useStyles = makeStyles({
  boxBody: {
    padding: 0,
  },
  accordionRoot: {
    padding: 0,
    '& > div': {
      borderWidth: '1px',
    },
    '& .MuiAccordion-root > .MuiButtonBase-root': {
      minHeight: '64px',
      paddingLeft: '24px',
      paddingRight: '24px',
    },
    '& .MuiAccordionDetails-root': {
      borderTop: '1px solid #dbd9d6',
      padding: 0,
    },
    '& .MuiAccordionSummary-content > h3': {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '24px',
    },
    '& .MuiAccordionSummary-content > .MuiSvgIcon-root': {
      height: '24px',
      width: '24px',
    },
    '& .slick-slide > div > div > div': {
      height: '100%',
    },
  },
  accordionBody: {
    paddingTop: '24px',
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '24px',
  },
  isCarousel: {
    paddingRight: '0',
  },
})

type PropTypes = {
  tabsItems: MemberBenefitTab[]
  productCode: string
}

const MobileTabs = ({ tabsItems, productCode }: PropTypes) => {
  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const isCallCustomerType =
    productCode && callCustomerCodes.includes(productCode)

  /******************************************
   * RENDERS
   ******************************************/

  const renderLabel = (label: string, tabIndex: number) => {
    const labelNode = <span>{label}</span>
    const labelContent =
      tabIndex === tabIndexes.COURSES && isCallCustomerType ? (
        <BadgedLabel label={labelNode} badgeText="UPGRADE" />
      ) : (
        labelNode
      )
    return <Typography component={'h3'}>{labelContent}</Typography>
  }

  const renderContentCarousel = (
    data: { [key: string]: AlgoliaContentPayload },
    link: string,
    index: string,
  ) => {
    const mergedData = Object.keys(data).reduce(
      (acc: IAbstractSearchItem[], key: string) => {
        const items = data[key]?.data
        return items && items.length ? [...acc, ...items] : acc
      },
      [],
    )

    if (mergedData.length <= 1) {
      return (
        <div className={classes.accordionBody}>
          {mergedData.length === 1 && (
            <>
              <CustomCardHit
                hit={{ ...mergedData[0], objectID: mergedData[0].id }}
                index={{ targetedIndex: index }}
              />
              <Spacer fullWidth size={24} />
            </>
          )}
          <Button variant="primary" href={link}>
            <span>View all</span>
          </Button>
        </div>
      )
    }

    return (
      <div
        className={clsx(classes.accordionBody, {
          [classes.isCarousel]: true,
        })}
      >
        <Carousel
          showDots
          sameHeight
          showBtn={
            <Button variant="primary" href={link}>
              <span>View all</span>
            </Button>
          }
          advancedItems={1}
        >
          {mergedData.map((hit: any) => {
            return (
              <CustomCardHit
                key={hit.id}
                hit={{ ...hit, objectID: hit.id }}
                index={{ targetedIndex: index }}
              />
            )
          })}
        </Carousel>
      </div>
    )
  }

  return (
    <Box
      classNames={{
        body: classes.boxBody,
      }}
      variant={'subModule'}
      header={{
        title: 'Member Benefits',
      }}
    >
      <Accordion
        className={clsx(classes.accordionRoot)}
        defaultPanel={0}
        iconSize={32}
        panels={[
          ...tabsItems.map(({ data, tab, tabIndex, index }) => {
            return {
              title: renderLabel(tabsConfig[tab].tabName, tabIndex),
              content: renderContentCarousel(data, tabsConfig[tab].link, index),
            }
          }),
          {
            title: renderLabel('Courses', tabIndexes.COURSES),
            content: (
              <div className={classes.accordionBody}>
                <Courses
                  variant={isCallCustomerType ? 'callCustomer' : 'launchLMS'}
                />
              </div>
            ),
          },
        ]}
      />
    </Box>
  )
}

export default MobileTabs
